import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useListClauses, useDeleteClause, getListClausesQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrg } from "@/lib/orgContext";
import { useActivePersona } from "@/lib/activePersona";
import { DraftingWizard } from "@/components/DraftingWizard";
import { Wand2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Search, BookOpen, Pencil, Trash2, ChevronRight, Clock, User } from "lucide-react";
import { useAuth } from "@clerk/react";
import { sanitizeHtml } from "@/lib/sanitize";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const CATEGORY_LABELS: Record<string, string> = {
  compensation: "Compensation",
  termination: "Termination",
  buyout: "Buyout",
  nil: "NIL",
  title_ix: "Title IX",
  indemnification: "Indemnification",
  governing_law: "Governing Law",
  outside_income: "Outside Income",
  general: "General",
};

const CATEGORY_COLORS: Record<string, string> = {
  compensation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  termination: "bg-red-50 text-red-700 border-red-200",
  buyout: "bg-orange-50 text-orange-700 border-orange-200",
  nil: "bg-purple-50 text-purple-700 border-purple-200",
  title_ix: "bg-blue-50 text-blue-700 border-blue-200",
  indemnification: "bg-slate-50 text-slate-700 border-slate-200",
  governing_law: "bg-gray-50 text-gray-700 border-gray-200",
  outside_income: "bg-yellow-50 text-yellow-700 border-yellow-200",
  general: "bg-muted text-muted-foreground border-border",
};

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "compensation", label: "Compensation" },
  { value: "termination", label: "Termination" },
  { value: "buyout", label: "Buyout" },
  { value: "nil", label: "NIL" },
  { value: "title_ix", label: "Title IX" },
  { value: "indemnification", label: "Indemnification" },
  { value: "governing_law", label: "Governing Law" },
  { value: "outside_income", label: "Outside Income" },
  { value: "general", label: "General" },
];

export default function ClauseLibrary() {
  const { org } = useOrg();
  const { toast } = useToast();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = org?.role === "admin";

  // Task #555: Drafting Wizard entry point. Only surface the button when
  // the active persona is Legal AND the per-member opt-in flag is true.
  // We fetch the prefs lazily here to keep the rest of the page unaware of
  // wizard state.
  const { effective: effectivePersona } = useActivePersona(org?.personas ?? [], org?.lastActivePersona ?? null);
  const [wizardEnabled, setWizardEnabled] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  useEffect(() => {
    if (effectivePersona !== "Legal") return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch("/api/auth/drafting-wizard-prefs", {
          headers: token ? { authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok || cancelled) return;
        const j = (await r.json()) as { enabled?: boolean };
        setWizardEnabled(Boolean(j.enabled));
      } catch {
        // Fail-open: wizard simply stays hidden.
      }
    })();
    return () => { cancelled = true; };
  }, [effectivePersona, getToken]);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  // Document-type filter for clauses. Most clauses are general-purpose
  // and apply to either contract kind, but legal/HR teams may tag a clause
  // as term-sheet-only (short, non-binding language) or contract-only
  // (full execution language). The filter is client-side because the
  // result set is small and clauseRefs are evaluated per-row.
  const [docKindFilter, setDocKindFilter] = useState<"all" | "contract" | "term_sheet">("all");

  const categoryParam = category !== "all" ? (category as import("@workspace/api-client-react").ListClausesCategory) : undefined;

  const { data: rawClauses = [], isLoading } = useListClauses(
    {
      ...(categoryParam ? { category: categoryParam } : {}),
      ...(search ? { search } : {}),
    },
    { query: { queryKey: getListClausesQueryKey({ category: categoryParam, search: search || undefined }) } }
  );

  const clauses = rawClauses.filter((c) => {
    if (docKindFilter === "all") return true;
    const k = (c as { documentKind?: string }).documentKind ?? "contract";
    return k === docKindFilter;
  });

  const deleteClause = useDeleteClause();

  const handleDelete = async (id: number, title: string) => {
    try {
      await deleteClause.mutateAsync({ id });
      toast({ title: "Clause deleted", description: `"${title}" has been removed from the library.` });
      queryClient.invalidateQueries({ queryKey: getListClausesQueryKey() });
    } catch (err) {
      handleApiError(toast, err, { fallbackTitle: "Could not delete clause" });
    }
  };

  const grouped = clauses.reduce<Record<string, typeof clauses>>((acc, c) => {
    const key = c.category;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  const groupKeys = Object.keys(grouped).sort();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="page-header">
          <div className="flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-muted-foreground" />
            <h1>Clause Library</h1>
          </div>
          <p>Reusable legal clauses with merge field support for contract drafting.</p>
        </div>
        <div className="flex items-center gap-2">
          {effectivePersona === "Legal" && wizardEnabled && (
            <Button variant="outline" onClick={() => setWizardOpen(true)} data-testid="button-drafting-wizard">
              <Wand2 className="h-4 w-4 mr-2" /> Drafting wizard
            </Button>
          )}
          {isAdmin && (
            <Button asChild>
              <Link href="/clause-library/new">
                <Plus className="h-4 w-4 mr-2" /> New Clause
              </Link>
            </Button>
          )}
        </div>
      </div>
      <DraftingWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onAccept={(body) => {
          toast({
            title: "Skeleton ready",
            description: "Cited skeleton drafted. Open a new clause and paste to continue.",
          });
          void navigator.clipboard?.writeText(body).catch(() => {});
        }}
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search clauses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={docKindFilter} onValueChange={(v) => setDocKindFilter(v as "all" | "contract" | "term_sheet")}>
          <SelectTrigger className="w-[180px]" data-testid="select-clause-doc-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Document Types</SelectItem>
            <SelectItem value="contract">Contracts only</SelectItem>
            <SelectItem value="term_sheet">Term sheets only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        // Standardized skeleton loading state — matches the
        // category-grouped clause cards layout so the page doesn't jump
        // when results arrive.
        <div className="space-y-6" data-testid="clause-library-loading">
          {[1, 2].map((g) => (
            <div key={g}>
              <div className="flex items-center gap-2 mb-3">
                <Skeleton className="h-4 w-32 skeleton-shimmer" />
                <Skeleton className="h-5 w-8 rounded-md skeleton-shimmer" />
              </div>
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Skeleton className="h-4 w-[180px] skeleton-shimmer" />
                            <Skeleton className="h-5 w-[80px] rounded-md skeleton-shimmer" />
                            <Skeleton className="h-5 w-[50px] rounded-md skeleton-shimmer" />
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            <Skeleton className="h-3 w-[100px] skeleton-shimmer" />
                            <Skeleton className="h-3 w-[120px] skeleton-shimmer" />
                            <Skeleton className="h-3 w-[80px] skeleton-shimmer" />
                          </div>
                          <Skeleton className="h-3 w-full skeleton-shimmer" />
                          <Skeleton className="h-3 w-3/4 skeleton-shimmer" />
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Skeleton className="h-8 w-8 rounded-md skeleton-shimmer" />
                          <Skeleton className="h-8 w-8 rounded-md skeleton-shimmer" />
                          <Skeleton className="h-8 w-8 rounded-md skeleton-shimmer" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : clauses.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground font-medium">No clauses found</p>
            {isAdmin && (
              <Button variant="outline" className="mt-4" asChild>
                <Link href="/clause-library/new"><Plus className="h-4 w-4 mr-2" /> Create your first clause</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupKeys.map((key) => (
            <div key={key}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  {CATEGORY_LABELS[key] ?? key}
                </h2>
                <Badge variant="outline" className="text-xs">{grouped[key].length}</Badge>
              </div>
              <div className="space-y-2">
                {grouped[key].map((clause) => (
                  <Card key={clause.id} className="hover:shadow-sm transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{clause.title}</span>
                            <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[clause.category] ?? ""}`}>
                              {CATEGORY_LABELS[clause.category] ?? clause.category}
                            </Badge>
                            {clause.isSeed && (
                              <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">Starter</Badge>
                            )}
                            {(clause as { documentKind?: string }).documentKind === "term_sheet" && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">Term Sheet</Badge>
                            )}
                            {clause.tags.map((tag) => (
                              <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                            ))}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            {clause.jurisdiction && (
                              <span>{clause.jurisdiction}</span>
                            )}
                            {clause.approvingAttorney && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" /> {clause.approvingAttorney}
                              </span>
                            )}
                            {clause.lastReviewedAt && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" /> Reviewed {format(new Date(clause.lastReviewedAt), "MMM d, yyyy")}
                              </span>
                            )}
                            <span>{clause.versionCount} version{clause.versionCount !== 1 ? "s" : ""}</span>
                          </div>
                          {clause.latestVersion && (
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2 leading-relaxed"
                               dangerouslySetInnerHTML={{ __html: sanitizeHtml(clause.latestVersion.body) }}
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" asChild className="h-8 w-8">
                            <Link href={`/clause-library/${clause.id}`}>
                              <ChevronRight className="h-4 w-4" />
                            </Link>
                          </Button>
                          {isAdmin && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                                <Link href={`/clause-library/${clause.id}/edit`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Link>
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10">
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete clause?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This permanently removes "{clause.title}" from the library. This action cannot be undone. Clauses that are already attached to contracts cannot be deleted.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(clause.id, clause.title)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
